import { forwardRef } from 'react';
import { combineClassNames } from '../../../Utils/ClassnameUtils';

export interface GWPRadioProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
	size?: 'sm' | 'md' | 'lg';
	label?: React.ReactNode;
	description?: React.ReactNode;
	invalid?: boolean;
	onCheckedChange?: (checked: boolean, event: React.ChangeEvent<HTMLInputElement>) => void;
}

const GWPRadio = forwardRef<HTMLInputElement, GWPRadioProps>((props, ref) => {
	const {
		className,
		size = 'md',
		label,
		description,
		invalid = false,
		onChange,
		onCheckedChange,
		disabled,
		...rest
	} = props;

	const rootClassName = combineClassNames(
		'gwp-radio',
		size,
		invalid ? 'invalid' : undefined,
		disabled ? 'disabled' : undefined,
		description !== undefined ? 'has-description' : undefined,
		className,
	);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		onChange?.(event);
		onCheckedChange?.(event.target.checked, event);
	};

	return (
		<label className={rootClassName}>
			<input
				{...rest}
				ref={ref}
				type="radio"
				disabled={disabled}
				aria-invalid={invalid || rest['aria-invalid']}
				className="gwp-radio-control"
				onChange={handleChange}
			/>
			<span className="gwp-radio-body">
				<span className="gwp-radio-indicator" aria-hidden="true" />
				{(label !== undefined || description !== undefined) && (
					<span className="gwp-radio-copy">
						{label !== undefined && <span className="gwp-radio-label">{label}</span>}
						{description !== undefined && <span className="gwp-radio-description">{description}</span>}
					</span>
				)}
			</span>
		</label>
	);
});

GWPRadio.displayName = 'GWPRadio';

export default GWPRadio;
